<template>
    <div class="squad-maturity-dashboard">
        <!-- Header -->
        <div class="dashboard-header">
            <h2>
                <i class="bi bi-graph-up"></i>
                Squad Maturity Dashboard
            </h2>
            <p class="subtitle">Monitor player development and training recommendations</p>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p>Loading squad maturity data...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
            <i class="bi bi-exclamation-triangle"></i>
            <p>{{ error }}</p>
            <button class="btn btn-primary" @click="loadSquadData">Retry</button>
        </div>

        <!-- Dashboard Content -->
        <div v-else class="dashboard-content">
            <!-- Summary Cards -->
            <div class="summary-cards">
                <div class="summary-card total">
                    <div class="card-icon">
                        <i class="bi bi-people"></i>
                    </div>
                    <div class="card-content">
                        <span class="count">{{ summary.total }}</span>
                        <span class="label">Total Players</span>
                    </div>
                </div>

                <div class="summary-card pre-phv">
                    <div class="card-icon">
                        <i class="bi bi-arrow-up-circle"></i>
                    </div>
                    <div class="card-content">
                        <span class="count">{{ summary.prePhv }}</span>
                        <span class="label">Pre-PHV</span>
                    </div>
                </div>

                <div class="summary-card circa-phv">
                    <div class="card-icon">
                        <i class="bi bi-exclamation-circle"></i>
                    </div>
                    <div class="card-content">
                        <span class="count">{{ summary.circaPhv }}</span>
                        <span class="label">Circa-PHV</span>
                    </div>
                </div>

                <div class="summary-card post-phv">
                    <div class="card-icon">
                        <i class="bi bi-check-circle"></i>
                    </div>
                    <div class="card-content">
                        <span class="count">{{ summary.postPhv }}</span>
                        <span class="label">Post-PHV</span>
                    </div>
                </div>

                <div class="summary-card not-assessed">
                    <div class="card-icon">
                        <i class="bi bi-question-circle"></i>
                    </div>
                    <div class="card-content">
                        <span class="count">{{ summary.notAssessed }}</span>
                        <span class="label">Not Assessed</span>
                    </div>
                </div>

                <div class="summary-card assessed-no-phv" v-if="summary.assessedNoPhv > 0">
                    <div class="card-icon">
                        <i class="bi bi-clipboard-check"></i>
                    </div>
                    <div class="card-content">
                        <span class="count">{{ summary.assessedNoPhv }}</span>
                        <span class="label">Need PHV</span>
                    </div>
                </div>

                <div class="summary-card injury-risk" v-if="summary.injuryRiskCount > 0">
                    <div class="card-icon">
                        <i class="bi bi-shield-exclamation"></i>
                    </div>
                    <div class="card-content">
                        <span class="count">{{ summary.injuryRiskCount }}</span>
                        <span class="label">Injury Risk</span>
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <div class="filters-section">
                <div class="filter-group">
                    <label>Filter by Position:</label>
                    <select v-model="filterPosition" class="form-select">
                        <option value="">All Positions</option>
                        <option value="Goalkeeper">Goalkeepers</option>
                        <option value="Defender">Defenders</option>
                        <option value="Midfielder">Midfielders</option>
                        <option value="Forward">Forwards</option>
                    </select>
                </div>

                <div class="filter-group">
                    <label>Filter by Maturity:</label>
                    <select v-model="filterMaturity" class="form-select">
                        <option value="">All Categories</option>
                        <option value="Pre-PHV">Pre-PHV</option>
                        <option value="Circa-PHV">Circa-PHV</option>
                        <option value="Post-PHV">Post-PHV</option>
                        <option value="Assessed (No PHV)">Assessed (No PHV)</option>
                        <option value="Not Assessed">Not Assessed</option>
                    </select>
                </div>

                <div class="filter-group">
                    <label>View Mode:</label>
                    <div class="btn-group view-toggle">
                        <button :class="['btn', viewMode === 'grouped' ? 'btn-primary' : 'btn-outline-primary']"
                            @click="viewMode = 'grouped'">
                            <i class="bi bi-collection"></i> Grouped
                        </button>
                        <button :class="['btn', viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary']"
                            @click="viewMode = 'table'">
                            <i class="bi bi-table"></i> Table
                        </button>
                    </div>
                </div>
            </div>

            <!-- Grouped View -->
            <div v-if="viewMode === 'grouped'" class="grouped-view">
                <!-- Pre-PHV Group -->
                <div v-if="filteredGrouped['Pre-PHV']?.length > 0" class="maturity-group pre-phv-group">
                    <div class="group-header">
                        <h3>
                            <span class="badge bg-info">Pre-PHV</span>
                            <span class="count">({{ filteredGrouped['Pre-PHV'].length }} players)</span>
                        </h3>
                        <p class="group-description">
                            Focus on technical development. Avoid high physical loads.
                        </p>
                    </div>
                    <div class="players-grid">
                        <PlayerMaturityCard v-for="player in filteredGrouped['Pre-PHV']" :key="player.userId"
                            :player="player" @click="openPlayerDetail(player)" />
                    </div>
                </div>

                <!-- Circa-PHV Group -->
                <div v-if="filteredGrouped['Circa-PHV']?.length > 0" class="maturity-group circa-phv-group">
                    <div class="group-header">
                        <h3>
                            <span class="badge bg-warning text-dark">Circa-PHV</span>
                            <span class="count">({{ filteredGrouped['Circa-PHV'].length }} players)</span>
                        </h3>
                        <p class="group-description">
                            ⚠️ Peak growth phase - Elevated injury risk. Reduce training loads and monitor closely.
                        </p>
                    </div>
                    <div class="players-grid">
                        <PlayerMaturityCard v-for="player in filteredGrouped['Circa-PHV']" :key="player.userId"
                            :player="player" @click="openPlayerDetail(player)" />
                    </div>
                </div>

                <!-- Post-PHV Group -->
                <div v-if="filteredGrouped['Post-PHV']?.length > 0" class="maturity-group post-phv-group">
                    <div class="group-header">
                        <h3>
                            <span class="badge bg-success">Post-PHV</span>
                            <span class="count">({{ filteredGrouped['Post-PHV'].length }} players)</span>
                        </h3>
                        <p class="group-description">
                            Ready for full training loads. Focus on physical development alongside technical.
                        </p>
                    </div>
                    <div class="players-grid">
                        <PlayerMaturityCard v-for="player in filteredGrouped['Post-PHV']" :key="player.userId"
                            :player="player" @click="openPlayerDetail(player)" />
                    </div>
                </div>

                <!-- Assessed (No PHV) Group - has bio-age but no sitting height -->
                <div v-if="filteredGrouped['Assessed (No PHV)']?.length > 0"
                    class="maturity-group assessed-no-phv-group">
                    <div class="group-header">
                        <h3>
                            <span class="badge bg-info text-dark">Assessed (No PHV)</span>
                            <span class="count">({{ filteredGrouped['Assessed (No PHV)'].length }} players)</span>
                        </h3>
                        <p class="group-description">
                            These players have bio-age data but need <strong>sitting height</strong> measurement for PHV
                            classification.
                        </p>
                    </div>
                    <div class="players-grid">
                        <PlayerMaturityCard v-for="player in filteredGrouped['Assessed (No PHV)']" :key="player.userId"
                            :player="player" @click="openPlayerDetail(player)" />
                    </div>
                </div>

                <!-- Not Assessed Group -->
                <div v-if="filteredGrouped['Not Assessed']?.length > 0" class="maturity-group not-assessed-group">
                    <div class="group-header">
                        <h3>
                            <span class="badge bg-secondary">Not Assessed</span>
                            <span class="count">({{ filteredGrouped['Not Assessed'].length }} players)</span>
                        </h3>
                        <p class="group-description">
                            These players need bio-banding assessment. Click to assess.
                        </p>
                    </div>
                    <div class="players-grid">
                        <PlayerMaturityCard v-for="player in filteredGrouped['Not Assessed']" :key="player.userId"
                            :player="player" :needsAssessment="true" @click="openPlayerDetail(player)" />
                    </div>
                </div>
            </div>

            <!-- Table View -->
            <div v-else class="table-view">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th @click="sortBy('playerName')" class="sortable">
                                Player
                                <i v-if="sortField === 'playerName'"
                                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                            </th>
                            <th @click="sortBy('position')" class="sortable">
                                Position
                                <i v-if="sortField === 'position'"
                                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                            </th>
                            <th @click="sortBy('decimalAge')" class="sortable">
                                Age
                                <i v-if="sortField === 'decimalAge'"
                                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                            </th>
                            <th @click="sortBy('bioAge')" class="sortable">
                                Bio Age
                                <i v-if="sortField === 'bioAge'"
                                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                            </th>
                            <th @click="sortBy('percentPAH')" class="sortable">
                                %PAH
                                <i v-if="sortField === 'percentPAH'"
                                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                            </th>
                            <th>Maturity</th>
                            <th @click="sortBy('growthVelocity')" class="sortable">
                                Growth Rate
                                <i v-if="sortField === 'growthVelocity'"
                                    :class="sortDirection === 'asc' ? 'bi bi-sort-up' : 'bi bi-sort-down'"></i>
                            </th>
                            <th>Risk</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="player in sortedPlayers" :key="player.userId"
                            :class="{ 'injury-risk-row': player.injuryRiskFlag }">
                            <td class="player-cell">
                                <img v-if="player.userImage" :src="getImgUrl(player.userImage)" :alt="player.playerName"
                                    class="player-avatar" />
                                <div v-else class="player-avatar-placeholder">
                                    {{ getInitials(player.playerName) }}
                                </div>
                                <span class="player-name">{{ player.playerName }}</span>
                            </td>
                            <td>
                                <span class="position-badge"
                                    :class="'position-' + player.generalPosition?.toLowerCase()">
                                    {{ player.position || '-' }}
                                </span>
                            </td>
                            <td>{{ formatNumber(player.decimalAge) || '-' }}</td>
                            <td>{{ formatNumber(player.bioAge) || '-' }}</td>
                            <td>{{ formatNumber(player.percentPAH) ? formatNumber(player.percentPAH) + '%' : '-' }}</td>
                            <td>
                                <span class="maturity-badge" :class="getMaturityClass(getPlayerMaturityStatus(player))">
                                    {{ getPlayerMaturityStatus(player) }}
                                </span>
                            </td>
                            <td>
                                <span v-if="formatNumber(player.growthVelocity)"
                                    :class="{ 'text-danger fw-bold': parseFloat(player.growthVelocity) > 6 }">
                                    {{ formatNumber(player.growthVelocity) }} cm/yr
                                    <i v-if="parseFloat(player.growthVelocity) > 6"
                                        class="bi bi-exclamation-triangle text-danger"></i>
                                </span>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                                <span v-if="player.injuryRiskFlag" class="badge bg-danger">
                                    <i class="bi bi-exclamation-triangle"></i> High
                                </span>
                                <span v-else-if="player.maturityCategory === 'Circa-PHV'"
                                    class="badge bg-warning text-dark">
                                    Medium
                                </span>
                                <span v-else class="badge bg-success">
                                    Low
                                </span>
                            </td>
                            <td>
                                <button class="btn btn-sm btn-outline-primary" @click="openPlayerDetail(player)">
                                    <i class="bi bi-eye"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Player Detail Modal -->
        <div v-if="selectedPlayer" class="player-detail-modal" @click.self="selectedPlayer = null">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>{{ selectedPlayer.playerName }}</h3>
                    <button class="btn-close" @click="selectedPlayer = null"></button>
                </div>
                <div class="modal-body">
                    <div class="player-info-grid">
                        <div class="info-item">
                            <label>Position</label>
                            <span>{{ selectedPlayer.position }} ({{ selectedPlayer.generalPosition }})</span>
                        </div>
                        <div class="info-item">
                            <label>Chronological Age</label>
                            <span>{{ formatNumber(selectedPlayer.decimalAge) || '-' }} years</span>
                        </div>
                        <div class="info-item">
                            <label>Biological Age</label>
                            <span>{{ formatNumber(selectedPlayer.bioAge) || '-' }} years</span>
                        </div>
                        <div class="info-item">
                            <label>% Predicted Adult Height</label>
                            <span>{{ formatNumber(selectedPlayer.percentPAH) || '-' }}%</span>
                        </div>
                        <div class="info-item">
                            <label>Maturity Status</label>
                            <span class="maturity-badge" :class="getMaturityClass(selectedPlayer.maturityCategory)">
                                {{ selectedPlayer.maturityCategory || 'Not Assessed' }}
                            </span>
                        </div>
                        <div class="info-item">
                            <label>Growth Velocity</label>
                            <span :class="{ 'text-danger': parseFloat(selectedPlayer.growthVelocity) > 6 }">
                                {{ formatNumber(selectedPlayer.growthVelocity) || '-' }} cm/year
                            </span>
                        </div>
                    </div>

                    <!-- Recommendations -->
                    <div v-if="selectedPlayerRecommendations" class="recommendations-section">
                        <h4>Training Recommendations</h4>

                        <div class="rec-block focus-areas">
                            <h5><i class="bi bi-bullseye"></i> Focus Areas</h5>
                            <ul>
                                <li v-for="area in selectedPlayerRecommendations.focusAreas" :key="area">
                                    {{ area }}
                                </li>
                            </ul>
                        </div>

                        <div v-if="selectedPlayerRecommendations.avoidAreas?.length" class="rec-block avoid-areas">
                            <h5><i class="bi bi-x-circle"></i> Areas to Limit/Avoid</h5>
                            <ul>
                                <li v-for="area in selectedPlayerRecommendations.avoidAreas" :key="area">
                                    {{ area }}
                                </li>
                            </ul>
                        </div>

                        <div v-if="selectedPlayerRecommendations.injuryRisks?.length" class="rec-block injury-risks">
                            <h5><i class="bi bi-shield-exclamation"></i> Common Injury Risks</h5>
                            <ul>
                                <li v-for="risk in selectedPlayerRecommendations.injuryRisks" :key="risk">
                                    {{ risk }}
                                </li>
                            </ul>
                        </div>

                        <div class="rec-block load-guidelines">
                            <h5><i class="bi bi-speedometer2"></i> Load Guidelines</h5>
                            <p>{{ selectedPlayerRecommendations.loadGuidelines }}</p>
                        </div>

                        <div v-if="selectedPlayerRecommendations.specificNote" class="rec-block specific-note">
                            <h5><i class="bi bi-person-badge"></i> Position-Specific Note</h5>
                            <p>{{ selectedPlayerRecommendations.specificNote }}</p>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-secondary" @click="selectedPlayer = null">Close</button>
                    <button class="btn btn-primary" @click="goToPlayerProfile(selectedPlayer.userId)">
                        View Full Profile
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import { getRecommendations } from "@/utils/biobandUtils.js";
import PlayerMaturityCard from "@/components/PlayerMaturityCard.vue";

export default {
    name: "SquadMaturityDashboard",
    components: {
        PlayerMaturityCard
    },
    props: {
        teamId: {
            type: [String, Number],
            required: true
        },
        userData: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: true,
            error: null,
            players: [],
            grouped: {},
            summary: {
                total: 0,
                prePhv: 0,
                circaPhv: 0,
                postPhv: 0,
                assessedNoPhv: 0,
                notAssessed: 0,
                injuryRiskCount: 0
            },
            filterPosition: '',
            filterMaturity: '',
            viewMode: 'grouped',
            sortField: 'playerName',
            sortDirection: 'asc',
            selectedPlayer: null
        };
    },
    computed: {
        filteredPlayers() {
            let result = [...this.players];

            if (this.filterPosition) {
                result = result.filter(p => p.generalPosition === this.filterPosition);
            }

            if (this.filterMaturity) {
                result = result.filter(p => {
                    const category = p.maturityCategory || (p.bioAge ? 'Assessed (No PHV)' : 'Not Assessed');
                    return category === this.filterMaturity;
                });
            }

            return result;
        },
        filteredGrouped() {
            const groups = {
                'Pre-PHV': [],
                'Circa-PHV': [],
                'Post-PHV': [],
                'Assessed (No PHV)': [],
                'Not Assessed': []
            };

            this.filteredPlayers.forEach(player => {
                let category = player.maturityCategory;

                if (category && groups[category]) {
                    groups[category].push(player);
                } else if (player.bioAge) {
                    // Has bio-age but no maturity category (no sitting height)
                    groups['Assessed (No PHV)'].push(player);
                } else {
                    groups['Not Assessed'].push(player);
                }
            });

            return groups;
        },
        sortedPlayers() {
            const sorted = [...this.filteredPlayers];

            sorted.sort((a, b) => {
                let aVal = a[this.sortField];
                let bVal = b[this.sortField];

                // Handle null/undefined
                if (aVal === null || aVal === undefined) aVal = '';
                if (bVal === null || bVal === undefined) bVal = '';

                // String comparison
                if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }

                if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
                if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
                return 0;
            });

            return sorted;
        },
        selectedPlayerRecommendations() {
            if (!this.selectedPlayer?.position || !this.selectedPlayer?.maturityCategory) {
                return null;
            }
            return getRecommendations(this.selectedPlayer.position, this.selectedPlayer.maturityCategory);
        }
    },
    mounted() {
        this.loadSquadData();
    },
    watch: {
        teamId: {
            handler() {
                this.loadSquadData();
            }
        }
    },
    methods: {
        getImgUrl(pic) {
            return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
        },
        async loadSquadData() {
            this.loading = true;
            this.error = null;

            try {
                const response = await PlayerPhysicalPerformance.getBioAgeResultsByTeamId(
                    this.teamId,
                    this.userData.id,
                    this.userData.userType
                );

                if (response.data.error) {
                    this.error = response.data.error;
                    return;
                }

                this.players = response.data.players || [];
                this.grouped = response.data.grouped || {};
                this.summary = response.data.summary || {
                    total: 0,
                    prePhv: 0,
                    circaPhv: 0,
                    postPhv: 0,
                    notAssessed: 0,
                    injuryRiskCount: 0
                };
            } catch (err) {
                console.error('Error loading squad maturity data:', err);
                this.error = 'Failed to load squad data. Please try again.';
            } finally {
                this.loading = false;
            }
        },

        sortBy(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortField = field;
                this.sortDirection = 'asc';
            }
        },

        getMaturityClass(category) {
            switch (category) {
                case 'Pre-PHV': return 'maturity-pre';
                case 'Circa-PHV': return 'maturity-circa';
                case 'Post-PHV': return 'maturity-post';
                case 'Assessed (No PHV)': return 'maturity-assessed-no-phv';
                default: return 'maturity-unknown';
            }
        },

        getPlayerMaturityStatus(player) {
            if (player.maturityCategory) {
                return player.maturityCategory;
            } else if (player.bioAge) {
                return 'Assessed (No PHV)';
            }
            return 'Not Assessed';
        },

        formatNumber(value, decimals = 1) {
            if (value === null || value === undefined || value === '') return null;
            const num = parseFloat(value);
            return isNaN(num) ? null : num.toFixed(decimals);
        },

        getInitials(name) {
            if (!name) return '?';
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        },

        openPlayerDetail(player) {
            this.selectedPlayer = player;
        },

        goToPlayerProfile(userId) {
            // Navigate to profile with player data as query parameter
            const playerData = this.players.find(p => p.userId === userId);
            if (playerData) {
                this.$router.push({
                    name: 'profile',
                    query: {
                        playerData: JSON.stringify({ id: userId })
                    }
                });
            } else {
                // Fallback: just navigate to profile
                this.$router.push({ name: 'profile' });
            }
        }
    }
};
</script>

<style scoped lang="scss">
.squad-maturity-dashboard {
    padding: 20px;
    width: 100%;

    .dashboard-content {

        .table-view {

            tbody {
                tr {
                    border-bottom: 1px solid #e0e0e0;

                    td.player-cell {
                        width: 100%
                    }

                    td {
                        border: none;
                    }
                }
            }

        }
    }


}

.dashboard-header {
    margin-bottom: 24px;
}

.dashboard-header h2 {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 4px;
}

.dashboard-header h2 i {
    margin-right: 8px;
    color: var(--primary-color, #007bff);
}

.dashboard-header .subtitle {
    color: #6c757d;
    margin: 0;
}

/* Loading & Error States */
.loading-state,
.error-state {
    text-align: center;
    padding: 60px 20px;
}

.loading-state .spinner-border {
    width: 3rem;
    height: 3rem;
}

.error-state i {
    font-size: 48px;
    color: #dc3545;
    margin-bottom: 16px;
}

/* Summary Cards */
.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.summary-card {
    display: flex;
    align-items: center;
    padding: 16px;
    border-radius: 12px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.summary-card .card-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    font-size: 20px;
}

.summary-card.total .card-icon {
    background: #e3f2fd;
    color: #1976d2;
}

.summary-card.pre-phv .card-icon {
    background: #e0f7fa;
    color: #0097a7;
}

.summary-card.circa-phv .card-icon {
    background: #fff3e0;
    color: #f57c00;
}

.summary-card.post-phv .card-icon {
    background: #e8f5e9;
    color: #388e3c;
}

.summary-card.not-assessed .card-icon {
    background: #f5f5f5;
    color: #757575;
}

.summary-card.assessed-no-phv .card-icon {
    background: #e3f2fd;
    color: #1976d2;
}

.summary-card.injury-risk .card-icon {
    background: #ffebee;
    color: #d32f2f;
}

.summary-card .card-content {
    display: flex;
    flex-direction: column;
}

.summary-card .count {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
}

.summary-card .label {
    font-size: 12px;
    color: #6c757d;
}

/* Filters */
.filters-section {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-end;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 12px;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.filter-group label {
    font-size: 12px;
    font-weight: 500;
    color: #6c757d;
}

.filter-group select {
    min-width: 160px;
}

.view-toggle .btn {
    display: flex;
    align-items: center;
    gap: 4px;
}

/* Grouped View */
.grouped-view {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.maturity-group {
    padding: 20px;
    border-radius: 12px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.pre-phv-group {
    border-left: 4px solid #0097a7;
}

.circa-phv-group {
    border-left: 4px solid #f57c00;
}

.post-phv-group {
    border-left: 4px solid #388e3c;
}

.assessed-no-phv-group {
    border-left: 4px solid #1976d2;
}

.not-assessed-group {
    border-left: 4px solid #757575;
}

.group-header {
    margin-bottom: 16px;
}

.group-header h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.group-header .count {
    font-size: 14px;
    font-weight: normal;
    color: #6c757d;
}

.group-description {
    font-size: 14px;
    color: #6c757d;
    margin: 0;
}

.players-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
}

/* Player Card */
.player-card {
    padding: 12px;
    border-radius: 8px;
    background: #f8f9fa;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
}

.player-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.player-card.injury-risk {
    border: 2px solid #ffcdd2;
    background: #fff5f5;
}

.player-card.needs-assessment {
    border: 2px dashed #bdbdbd;
    background: white;
}

.player-card .card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.player-card .avatar,
.player-card .avatar-placeholder {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
}

.player-card .avatar-placeholder {
    background: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #616161;
}

.player-card .player-info {
    flex: 1;
    min-width: 0;
}

.player-card .name {
    display: block;
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.player-card .position {
    display: block;
    font-size: 12px;
    color: #6c757d;
}

.player-card .risk-indicator {
    flex-shrink: 0;
}

.player-card .card-body {
    display: flex;
    gap: 12px;
}

.player-card .stat {
    display: flex;
    flex-direction: column;
}

.player-card .stat label {
    font-size: 10px;
    color: #9e9e9e;
    text-transform: uppercase;
}

.player-card .stat span {
    font-size: 14px;
    font-weight: 600;
}

.player-card .assess-prompt {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #1976d2;
    font-size: 13px;
}

/* Table View */
.table-view {
    background: white;
    border-radius: 12px;
    overflow-x: auto;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.table-view .table {
    margin-bottom: 0;
    width: 100%;
}

.table-view th,
.table-view td {
    vertical-align: middle;
    padding: 12px 8px;
}

.table-view th {
    background: #f8f9fa;
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
}

/* Column widths for proper alignment */
.table-view th:nth-child(1),
.table-view td:nth-child(1) {
    width: 180px;
    min-width: 150px;
}

.table-view th:nth-child(2),
.table-view td:nth-child(2) {
    width: 100px;
}

.table-view th:nth-child(3),
.table-view td:nth-child(3),
.table-view th:nth-child(4),
.table-view td:nth-child(4),
.table-view th:nth-child(5),
.table-view td:nth-child(5) {
    width: 70px;
    text-align: center;
}

.table-view th:nth-child(6),
.table-view td:nth-child(6) {
    width: 120px;
}

.table-view th:nth-child(7),
.table-view td:nth-child(7) {
    width: 100px;
}

.table-view th:nth-child(8),
.table-view td:nth-child(8) {
    width: 80px;
    text-align: center;
}

.table-view th:nth-child(9),
.table-view td:nth-child(9) {
    width: 70px;
    text-align: center;
}

.table-view th.sortable {
    cursor: pointer;
    user-select: none;
}

.table-view th.sortable:hover {
    background: #e9ecef;
}

.player-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
}

.player-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
}

.player-avatar {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    object-fit: cover;
}

.player-avatar-placeholder {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    background: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    color: #616161;
}

.position-badge {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.position-goalkeeper {
    background: #fff3e0;
    color: #e65100;
}

.position-defender {
    background: #e3f2fd;
    color: #1565c0;
}

.position-midfielder {
    background: #e8f5e9;
    color: #2e7d32;
}

.position-forward {
    background: #fce4ec;
    color: #c2185b;
}

.maturity-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
}

.maturity-pre {
    background: #e0f7fa;
    color: #006064;
}

.maturity-circa {
    background: #fff3e0;
    color: #e65100;
}

.maturity-post {
    background: #e8f5e9;
    color: #1b5e20;
}

.maturity-unknown {
    background: #f5f5f5;
    color: #616161;
}

.maturity-assessed-no-phv {
    background: #e3f2fd;
    color: #1565c0;
}

.injury-risk-row {
    background: #fff5f5 !important;
}

/* Modal */
.player-detail-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
}

.player-detail-modal .modal-content {
    background: white;
    border-radius: 16px;
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
}

.player-detail-modal .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
}

.player-detail-modal .modal-header h3 {
    margin: 0;
    font-size: 20px;
}

.player-detail-modal .modal-body {
    padding: 20px;
}

.player-info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 24px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.info-item label {
    font-size: 12px;
    color: #6c757d;
    text-transform: uppercase;
}

.info-item span {
    font-size: 15px;
    font-weight: 500;
}

.recommendations-section h4 {
    font-size: 18px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e0e0e0;
}

.rec-block {
    margin-bottom: 16px;
    padding: 12px;
    border-radius: 8px;
    background: #f8f9fa;
}

.rec-block h5 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.rec-block ul {
    margin: 0;
    padding-left: 20px;
}

.rec-block li {
    font-size: 14px;
    margin-bottom: 4px;
}

.rec-block p {
    font-size: 14px;
    margin: 0;
}

.rec-block.focus-areas {
    border-left: 4px solid #4caf50;
}

.rec-block.avoid-areas {
    border-left: 4px solid #f44336;
}

.rec-block.injury-risks {
    border-left: 4px solid #ff9800;
}

.rec-block.load-guidelines {
    border-left: 4px solid #2196f3;
}

.rec-block.specific-note {
    border-left: 4px solid #9c27b0;
}

.player-detail-modal .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid #e0e0e0;
}

/* Responsive */
@media (max-width: 768px) {
    .summary-cards {
        grid-template-columns: repeat(2, 1fr);
    }

    .filters-section {
        flex-direction: column;
        align-items: stretch;
    }

    .filter-group select {
        width: 100%;
    }

    .players-grid {
        grid-template-columns: 1fr;
    }

    .player-info-grid {
        grid-template-columns: 1fr;
    }

    /* Table responsive adjustments */
    .table-view .table {
        table-layout: auto;
        min-width: 900px;
    }

    .table-view th:nth-child(1),
    .table-view td:nth-child(1) {
        width: auto;
        min-width: 140px;
    }

    .player-name {
        max-width: 100px;
    }
}

@media (max-width: 576px) {
    .table-view {
        margin: 0 -1rem;
        border-radius: 0;
    }

    .table-view th,
    .table-view td {
        padding: 8px 6px;
        font-size: 12px;
    }

    .player-avatar,
    .player-avatar-placeholder {
        width: 28px;
        height: 28px;
        min-width: 28px;
    }

    .player-name {
        max-width: 80px;
        font-size: 12px;
    }

    .maturity-badge {
        padding: 2px 6px;
        font-size: 10px;
    }

    .position-badge {
        padding: 2px 4px;
        font-size: 10px;
    }
}
</style>
